<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import BlockedContactsAPI from 'dashboard/api/blockedContacts';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisContactSearch from 'dashboard/components-next/LaisContactSearch.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const SOURCE_OPTIONS = [
  { id: 'blocked_contacts_console', label: 'Console de bloqueios' },
  { id: 'lead_profile', label: 'Perfil do cliente' },
  { id: 'blocked_contacts_api', label: 'API de contatos bloqueados' },
];

const MOTIVO_OPTIONS = [
  { id: 'lgpd', label: 'Pedido LGPD / opt-out' },
  { id: 'spam', label: 'Spam' },
  { id: 'atividade_fraudulenta', label: 'Atividade fraudulenta' },
  { id: 'assedio', label: 'Assédio' },
  { id: 'erro_operacional', label: 'Erro operacional' },
  { id: 'outro', label: 'Outro' },
];

const route = useRoute();
const records = ref([]);
const meta = ref({ count: 0, permissions: {} });
const filters = ref({ q: '', source: '', expiration: '' });
const isLoading = ref(false);
const isCreating = ref(false);
const isUnblocking = ref(false);
const loadError = ref(false);
const selectedContact = ref(null);
const contactQuery = ref('');
const blockForm = ref({
  motivo: '',
  source: 'blocked_contacts_console',
  expires_at: '',
  observacoes: '',
});
const unblockTarget = ref(null);
const unblockReason = ref('');

const accountId = computed(() => route.params.accountId);
const capabilityError = ref(null);
const currentAccount = useMapGetter('getCurrentAccount');
// FE-AUD-107: gate negativo de permissao antes de buscar contatos bloqueados
// (PII sensivel). A capability canonica de clientes autoriza a leitura; um 403
// do backend marca o contexto como negado e impede novas cargas.
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.LEADS_INDEX,
    account: currentAccount.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const canViewBlocked = computed(
  () => capabilitiesContext.value.actions.view?.visible !== false
);
const permissions = computed(() => meta.value?.permissions || {});
const canCreate = computed(() => permissions.value.can_create !== false);
const canDestroy = computed(() => permissions.value.can_destroy !== false);
const hasSensitiveContactView = computed(
  () => permissions.value.can_view_sensitive_contact === true
);
const hasRecords = computed(() => records.value.length > 0);
const selectedBlocked = computed(
  () => selectedContact.value?.blocked || selectedContact.value?.blocked_contact
);
const filteredRecords = computed(() => {
  const query = filters.value.q.trim().toLowerCase();

  return records.value.filter(record => {
    const searchable = [
      record.contact?.name,
      record.contact?.email,
      record.contact?.phone_number,
      record.motivo,
      record.observacoes,
      record.source_label,
      record.blocked_by_user_name,
      record.contact?.id,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (query && !searchable.includes(query)) return false;
    if (filters.value.source && record.source !== filters.value.source) {
      return false;
    }
    if (
      filters.value.expiration &&
      record.expiration_status !== filters.value.expiration
    ) {
      return false;
    }
    return true;
  });
});

const stats = computed(() => [
  {
    key: 'total',
    label: 'Bloqueados',
    value: meta.value.count ?? records.value.length,
    icon: 'i-lucide-ban',
  },
  {
    key: 'manual',
    label: 'Via console',
    value: records.value.filter(
      record => record.source === 'blocked_contacts_console'
    ).length,
    icon: 'i-lucide-monitor-cog',
  },
  {
    key: 'expiring',
    label: 'Com expiração',
    value: records.value.filter(record => record.expires_at).length,
    icon: 'i-lucide-calendar-clock',
  },
  {
    key: 'redaction',
    label: 'Visibilidade',
    value: hasSensitiveContactView.value ? 'completa' : 'redigida',
    icon: hasSensitiveContactView.value ? 'i-lucide-eye' : 'i-lucide-eye-off',
  },
]);

const sourceLabel = source =>
  SOURCE_OPTIONS.find(option => option.id === source)?.label ||
  source ||
  'Origem não registrada';
const motivoLabel = motivo =>
  MOTIVO_OPTIONS.find(option => option.id === motivo)?.label ||
  motivo ||
  'Sem motivo';

const formatDateTime = value => {
  if (!value) return 'Sem expiração';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Data inválida';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const expirationClass = record => {
  if (record.expiration_status === 'expired') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  if (record.expiration_status === 'active') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  return 'border-lais-border bg-lais-bg-soft text-lais-text-muted';
};

const contactIdentity = contact =>
  [contact?.phone_number, contact?.email].filter(Boolean).join(' · ') ||
  'Contato redigido';

const contactName = contact => contact?.name || `Contato #${contact?.id}`;
const recordContactLink = record => ({
  name: 'lais_lead_historico',
  params: { accountId: accountId.value, id: record.contact?.id },
});

const expiresAtPayload = () => {
  if (!blockForm.value.expires_at) return '';
  const parsed = new Date(blockForm.value.expires_at);
  return Number.isNaN(parsed.getTime())
    ? blockForm.value.expires_at
    : parsed.toISOString();
};

const loadRecords = async () => {
  if (!canViewBlocked.value) {
    records.value = [];
    capabilityError.value = { status: 403 };
    return;
  }

  isLoading.value = true;
  loadError.value = false;

  try {
    const { data } = await BlockedContactsAPI.list();
    records.value = data?.payload || [];
    meta.value = data?.meta || { count: records.value.length };
    capabilityError.value = null;
  } catch (error) {
    records.value = [];
    loadError.value = true;
    if (error?.response?.status === 403) capabilityError.value = error;
    useAlert('Não foi possível carregar contatos bloqueados.');
  } finally {
    isLoading.value = false;
  }
};

const resetBlockForm = () => {
  selectedContact.value = null;
  contactQuery.value = '';
  blockForm.value = {
    motivo: '',
    source: 'blocked_contacts_console',
    expires_at: '',
    observacoes: '',
  };
};

const createBlockedContact = async () => {
  if (!canCreate.value) {
    useAlert('Seu perfil não permite criar bloqueios.');
    return;
  }
  if (!selectedContact.value?.id) {
    useAlert('Selecione um contato para bloquear.');
    return;
  }
  if (selectedBlocked.value) {
    useAlert('Este contato já está bloqueado.');
    return;
  }
  if (!blockForm.value.motivo) {
    useAlert('Selecione um motivo de bloqueio.');
    return;
  }

  isCreating.value = true;
  try {
    const { data } = await BlockedContactsAPI.create({
      contact_id: selectedContact.value.id,
      motivo: blockForm.value.motivo,
      observacoes: blockForm.value.observacoes.trim(),
      source: blockForm.value.source,
      expires_at: expiresAtPayload(),
    });
    records.value = [
      data.payload,
      ...records.value.filter(r => r.id !== data.payload.id),
    ];
    meta.value = {
      ...meta.value,
      count: records.value.length,
    };
    resetBlockForm();
    useAlert('Contato bloqueado com sucesso.');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível bloquear este contato.'
    );
  } finally {
    isCreating.value = false;
  }
};

const openUnblockModal = record => {
  unblockTarget.value = record;
  unblockReason.value = '';
};

const closeUnblockModal = () => {
  unblockTarget.value = null;
  unblockReason.value = '';
};

const confirmUnblock = async () => {
  const reason = unblockReason.value.trim();
  if (!reason) {
    useAlert('Informe o motivo do desbloqueio.');
    return;
  }

  isUnblocking.value = true;
  try {
    await BlockedContactsAPI.destroy(unblockTarget.value.id, {
      motivo_desbloqueio: reason,
    });
    records.value = records.value.filter(
      record => record.id !== unblockTarget.value.id
    );
    meta.value = {
      ...meta.value,
      count: records.value.length,
    };
    closeUnblockModal();
    useAlert('Contato desbloqueado com auditoria registrada.');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível desbloquear este contato.'
    );
  } finally {
    isUnblocking.value = false;
  }
};

onMounted(loadRecords);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full min-h-0 flex-col bg-white font-redHatText">
    <div v-if="!canViewBlocked" class="min-h-0 flex-1 overflow-auto px-4 py-8">
      <LaisPermissionState variant="denied" />
    </div>
    <template v-else>
      <LaisHeader
        title="Contatos bloqueados"
        helper="Console operacional para governança de bloqueios, motivos e reversões auditadas."
        :breadcrumb="[
          { label: 'Clientes', to: { name: 'lais_leads_index' } },
          { label: 'Contatos bloqueados' },
        ]"
        sticky
      >
        <template #actions>
          <LaisPrimaryButton
            icon="i-lucide-refresh-cw"
            variant="ghost"
            :is-loading="isLoading"
            @click="loadRecords"
          >
            Atualizar
          </LaisPrimaryButton>
        </template>
      </LaisHeader>

      <div
        class="min-h-0 flex-1 overflow-auto bg-lais-bg px-4 py-5 sm:px-6 lg:px-10"
      >
        <div class="grid gap-5">
          <section class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <article
              v-for="stat in stats"
              :key="stat.key"
              class="rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p
                    class="text-[12px] font-semibold uppercase text-lais-text-muted"
                  >
                    {{ stat.label }}
                  </p>
                  <p class="mt-2 text-[22px] font-semibold text-lais-text">
                    {{ stat.value }}
                  </p>
                </div>
                <span
                  class="grid size-9 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
                  aria-hidden="true"
                >
                  <span class="size-4" :class="stat.icon" />
                </span>
              </div>
            </article>
          </section>

          <div
            v-if="!hasSensitiveContactView"
            class="rounded-lais-sm border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
          >
            <p class="font-semibold">Dados sensíveis redigidos</p>
            <p class="mt-1">
              Seu perfil pode operar bloqueios, mas telefone, e-mail e nomes são
              mascarados nesta lista.
            </p>
          </div>

          <section
            v-if="canCreate"
            class="grid gap-4 rounded-lais border border-lais-border bg-white p-4 shadow-lais-card lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]"
          >
            <div class="grid gap-4">
              <div>
                <h2 class="text-[16px] font-semibold text-lais-text">
                  Criar bloqueio
                </h2>
                <p class="mt-1 text-[13px] text-lais-text-body">
                  Selecione o contato e registre motivo, origem e expiração.
                </p>
              </div>
              <LaisContactSearch
                v-model="selectedContact"
                v-model:query="contactQuery"
                label="Contato"
                helper="Busca restrita aos contatos desta conta."
                placeholder="Nome, telefone ou e-mail"
                @error="useAlert('Não foi possível buscar contatos.')"
              />
            </div>

            <form
              class="grid content-start gap-3"
              @submit.prevent="createBlockedContact"
            >
              <label
                class="grid gap-1 text-[13px] font-semibold text-lais-text"
              >
                Motivo
                <select
                  v-model="blockForm.motivo"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option value="" disabled>Selecione o motivo</option>
                  <option
                    v-for="motivo in MOTIVO_OPTIONS"
                    :key="motivo.id"
                    :value="motivo.id"
                  >
                    {{ motivo.label }}
                  </option>
                </select>
              </label>
              <label
                class="grid gap-1 text-[13px] font-semibold text-lais-text"
              >
                Origem
                <select
                  v-model="blockForm.source"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option
                    v-for="source in SOURCE_OPTIONS"
                    :key="source.id"
                    :value="source.id"
                  >
                    {{ source.label }}
                  </option>
                </select>
              </label>
              <label
                class="grid gap-1 text-[13px] font-semibold text-lais-text"
              >
                Expira em
                <input
                  v-model="blockForm.expires_at"
                  type="datetime-local"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="grid gap-1 text-[13px] font-semibold text-lais-text"
              >
                Observações
                <textarea
                  v-model="blockForm.observacoes"
                  rows="3"
                  class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
                  placeholder="Contexto operacional do bloqueio"
                />
              </label>
              <div
                v-if="selectedBlocked"
                class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-900"
              >
                Este contato já possui bloqueio ativo.
              </div>
              <LaisPrimaryButton
                type="submit"
                icon="i-lucide-ban"
                :is-loading="isCreating"
                :disabled="selectedBlocked"
              >
                Bloquear contato
              </LaisPrimaryButton>
            </form>
          </section>

          <section
            class="rounded-lais border border-lais-border bg-white shadow-lais-card"
          >
            <div
              class="grid gap-3 border-b border-lais-border px-4 py-4 lg:grid-cols-[minmax(0,1fr)_180px_180px]"
            >
              <label
                class="grid gap-1 text-[12px] font-semibold uppercase text-lais-text-muted"
              >
                Buscar
                <input
                  v-model="filters.q"
                  type="search"
                  placeholder="Cliente, motivo, ator ou contato"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal normal-case text-lais-text focus:border-lais-primary focus:outline-none"
                />
              </label>
              <label
                class="grid gap-1 text-[12px] font-semibold uppercase text-lais-text-muted"
              >
                Origem
                <select
                  v-model="filters.source"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal normal-case text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option value="">Todas</option>
                  <option
                    v-for="source in SOURCE_OPTIONS"
                    :key="source.id"
                    :value="source.id"
                  >
                    {{ source.label }}
                  </option>
                </select>
              </label>
              <label
                class="grid gap-1 text-[12px] font-semibold uppercase text-lais-text-muted"
              >
                Expiração
                <select
                  v-model="filters.expiration"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-normal normal-case text-lais-text focus:border-lais-primary focus:outline-none"
                >
                  <option value="">Todas</option>
                  <option value="active">Ativas</option>
                  <option value="expired">Expiradas</option>
                  <option value="none">Sem expiração</option>
                </select>
              </label>
            </div>

            <div
              v-if="isLoading"
              class="grid min-h-[240px] place-items-center text-[14px] text-lais-text-muted"
            >
              Carregando contatos bloqueados...
            </div>
            <div
              v-else-if="loadError"
              class="grid min-h-[240px] place-items-center px-4 text-center"
            >
              <div>
                <p class="text-[15px] font-semibold text-lais-text">
                  Contatos bloqueados indisponíveis
                </p>
                <p class="mt-1 text-[13px] text-lais-text-body">
                  Atualize para tentar carregar o console novamente.
                </p>
              </div>
            </div>
            <div
              v-else-if="!hasRecords"
              class="grid min-h-[240px] place-items-center px-4 text-center"
            >
              <div>
                <span
                  class="mx-auto grid size-10 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
                  aria-hidden="true"
                >
                  <span class="i-lucide-shield-check size-5" />
                </span>
                <p class="mt-3 text-[15px] font-semibold text-lais-text">
                  Nenhum contato bloqueado
                </p>
                <p class="mt-1 text-[13px] text-lais-text-body">
                  Bloqueios criados pelo perfil ou por este console aparecerão
                  aqui.
                </p>
              </div>
            </div>
            <div
              v-else-if="!filteredRecords.length"
              class="grid min-h-[220px] place-items-center px-4 text-center"
            >
              <div>
                <p class="text-[15px] font-semibold text-lais-text">
                  Nenhum bloqueio corresponde aos filtros
                </p>
                <p class="mt-1 text-[13px] text-lais-text-body">
                  Limpe a busca, origem ou expiração para ver mais registros.
                </p>
              </div>
            </div>
            <div v-else class="divide-y divide-lais-border">
              <article
                v-for="record in filteredRecords"
                :key="record.id"
                class="grid gap-4 px-4 py-4 xl:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(180px,1fr)_auto]"
              >
                <div class="min-w-0">
                  <div class="flex min-w-0 items-start gap-3">
                    <span
                      class="grid size-10 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
                      aria-hidden="true"
                    >
                      <span class="i-lucide-user-round-x size-5" />
                    </span>
                    <div class="min-w-0">
                      <p
                        class="truncate text-[14px] font-semibold text-lais-text"
                      >
                        {{ contactName(record.contact) }}
                      </p>
                      <p class="mt-1 truncate text-[12px] text-lais-text-muted">
                        #{{ record.contact?.id }} ·
                        {{ contactIdentity(record.contact) }}
                      </p>
                      <span
                        v-if="record.contact?.redacted"
                        class="mt-2 inline-flex rounded-full bg-lais-bg-soft px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
                      >
                        Redigido por permissão
                      </span>
                      <RouterLink
                        class="mt-2 inline-flex text-[12px] font-semibold text-lais-primary hover:text-lais-primary-600"
                        :to="recordContactLink(record)"
                      >
                        Abrir perfil
                      </RouterLink>
                    </div>
                  </div>
                </div>

                <dl
                  class="grid content-start gap-2 text-[12px] text-lais-text-body"
                >
                  <div>
                    <dt class="font-semibold uppercase text-lais-text-muted">
                      Motivo
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{ motivoLabel(record.motivo) }}
                    </dd>
                  </div>
                  <div v-if="record.observacoes">
                    <dt class="font-semibold uppercase text-lais-text-muted">
                      Observações
                    </dt>
                    <dd class="mt-1 line-clamp-2">
                      {{ record.observacoes }}
                    </dd>
                  </div>
                </dl>

                <dl
                  class="grid content-start gap-2 text-[12px] text-lais-text-body"
                >
                  <div>
                    <dt class="font-semibold uppercase text-lais-text-muted">
                      Origem
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{ record.source_label || sourceLabel(record.source) }}
                    </dd>
                  </div>
                  <div>
                    <dt class="font-semibold uppercase text-lais-text-muted">
                      Expiração
                    </dt>
                    <dd
                      class="mt-1 inline-flex rounded-full border px-2 py-1 font-semibold"
                      :class="expirationClass(record)"
                    >
                      {{ formatDateTime(record.expires_at) }}
                    </dd>
                  </div>
                  <div>
                    <dt class="font-semibold uppercase text-lais-text-muted">
                      Ator
                    </dt>
                    <dd class="mt-1 font-semibold text-lais-text">
                      {{ record.blocked_by_user_name || 'Sistema' }}
                    </dd>
                  </div>
                </dl>

                <div class="flex items-start justify-end gap-2">
                  <button
                    type="button"
                    class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-danger px-3 text-[13px] font-semibold text-lais-danger transition-colors hover:bg-lais-danger hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="!canDestroy"
                    @click="openUnblockModal(record)"
                  >
                    <span
                      class="i-lucide-unlock-keyhole size-4"
                      aria-hidden="true"
                    />
                    Desbloquear
                  </button>
                </div>
              </article>
            </div>
          </section>
        </div>
      </div>

      <LaisConfirmModal
        :show="Boolean(unblockTarget)"
        title="Desbloquear contato?"
        description="A reversão remove o bloqueio e registra o ator com o motivo informado."
        confirm-text="Desbloquear contato"
        confirm-variant="primary"
        :is-loading="isUnblocking"
        @close="closeUnblockModal"
        @confirm="confirmUnblock"
      >
        <div class="mt-4 grid gap-2">
          <p class="text-[13px] font-semibold text-lais-text">
            {{ contactName(unblockTarget?.contact) }}
          </p>
          <label class="grid gap-1 text-[13px] font-semibold text-lais-text">
            Motivo do desbloqueio
            <textarea
              v-model="unblockReason"
              rows="3"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Descreva por que o contato pode voltar a receber atendimento"
            />
          </label>
        </div>
      </LaisConfirmModal>
    </template>
  </section>
</template>
